import React from 'react';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';

/**
 * Privacy Policy.
 *
 * Describes what the platform actually does: email-OTP registration, speech and
 * written submissions scored by AI, payments handled entirely by Razorpay, and
 * contact-form messages stored for support. Keep this in step with the product -
 * an inaccurate privacy policy is worse than a thin one.
 */
const PrivacyPage = () => {
  return (
    <div className="min-h-screen">
      <Header />

      <main className="pt-24 pb-16">
        <section className="section">
          <div className="max-w-3xl mx-auto">
            <h1 className="heading-1 mb-4">Privacy Policy</h1>
            <p className="text-[#83928c] mb-8">Last updated: September 2026</p>

            <div className="prose prose-lg max-w-none">
              <div className="space-y-8">
                <div>
                  <h2 className="heading-3 mb-4">1. Who We Are</h2>
                  <p className="text-[#83928c]">
                    Tenses is operated by TALENTSCALE INDIA PRIVATE LIMITED. This policy explains what
                    personal information we collect when you use the platform, why we collect it, and
                    what control you have over it. It applies to our website, web application and
                    mobile application.
                  </p>
                </div>

                <div>
                  <h2 className="heading-3 mb-4">2. Information We Collect</h2>
                  <p className="text-[#83928c] mb-4">
                    We collect only what we need to run the service:
                  </p>
                  <ul className="list-disc list-inside text-[#83928c] space-y-2">
                    <li>
                      <strong className="text-[#1a1a2e]">Account information</strong> — your name,
                      email address and a securely hashed password. We never store your password in
                      readable form. Registration is confirmed by a one-time code sent to your email.
                    </li>
                    <li>
                      <strong className="text-[#1a1a2e]">Learning data</strong> — your scores,
                      attempts, progress and assessment results across the modules.
                    </li>
                    <li>
                      <strong className="text-[#1a1a2e]">Speech recordings</strong> — audio captured
                      by the speaking modules and the AI interview simulator, so your pronunciation
                      and fluency can be scored.
                    </li>
                    <li>
                      <strong className="text-[#1a1a2e]">Written submissions</strong> — essays,
                      emails and other text you enter for evaluation.
                    </li>
                    <li>
                      <strong className="text-[#1a1a2e]">Subscription records</strong> — which plan
                      you hold, when it started and when it ends.
                    </li>
                    <li>
                      <strong className="text-[#1a1a2e]">Support messages</strong> — anything you
                      send through our contact form or by email, so we can reply and follow up.
                    </li>
                    <li>
                      <strong className="text-[#1a1a2e]">Technical data</strong> — basic log
                      information such as device type, browser and timestamps, used to keep the
                      service secure and working.
                    </li>
                  </ul>
                </div>

                <div>
                  <h2 className="heading-3 mb-4">3. Payment Information</h2>
                  <p className="text-[#83928c]">
                    Payments are processed by Razorpay. Your card, UPI or banking details are entered
                    directly with them and are never sent to or stored on our servers. We keep only
                    the record of a transaction — its reference, amount, status and the plan it
                    relates to. Razorpay handles that payment data under its own privacy policy.
                  </p>
                </div>

                <div>
                  <h2 className="heading-3 mb-4">4. How We Use Your Information</h2>
                  <ul className="list-disc list-inside text-[#83928c] space-y-2">
                    <li>To create and secure your account and verify your email address</li>
                    <li>To score your speaking and writing, and to show you your results and progress</li>
                    <li>To issue and verify TPI certification</li>
                    <li>To activate and manage the plan you have purchased</li>
                    <li>To reply to your support requests</li>
                    <li>To send service messages such as password resets and payment confirmations</li>
                    <li>To detect misuse, protect the platform and meet our legal obligations</li>
                  </ul>
                  <p className="text-[#83928c] mt-4">
                    We do not sell your personal information, and we do not use your speech
                    recordings or written submissions for advertising.
                  </p>
                </div>

                <div>
                  <h2 className="heading-3 mb-4">5. Who We Share It With</h2>
                  <p className="text-[#83928c] mb-4">
                    We share personal information only with the service providers needed to operate
                    Tenses, and only to the extent they need it:
                  </p>
                  <ul className="list-disc list-inside text-[#83928c] space-y-2">
                    <li>Our payment gateway, to take payments and confirm them</li>
                    <li>Our cloud hosting and database providers, to run and store the service</li>
                    <li>Our email provider, to send verification, reset and notification emails</li>
                    <li>Speech and language processing services, to score your submissions</li>
                  </ul>
                  <p className="text-[#83928c] mt-4">
                    If you use Tenses through a college or employer, your scores and progress are
                    visible to the administrators of that organisation. We may also disclose
                    information where required by law.
                  </p>
                </div>

                <div>
                  <h2 className="heading-3 mb-4">6. How Long We Keep It</h2>
                  <p className="text-[#83928c]">
                    Account and learning data is kept while your account is active so your history and
                    certification stay available to you. Speech recordings and written submissions are
                    retained only as long as needed to produce and support your scores. Transaction
                    records are kept for as long as tax and accounting law requires. When you ask us
                    to delete your account, we remove your personal data except anything we are
                    legally obliged to retain.
                  </p>
                </div>

                <div>
                  <h2 className="heading-3 mb-4">7. Security</h2>
                  <p className="text-[#83928c]">
                    Data is encrypted in transit, passwords are stored only as salted hashes, and
                    access to production systems is restricted to the people who need it. No system
                    can be guaranteed completely secure, but if a breach ever affects your personal
                    data we will act promptly and tell you where we are required to.
                  </p>
                </div>

                <div>
                  <h2 className="heading-3 mb-4">8. Your Rights</h2>
                  <p className="text-[#83928c] mb-4">You may ask us to:</p>
                  <ul className="list-disc list-inside text-[#83928c] space-y-2">
                    <li>Give you a copy of the personal data we hold about you</li>
                    <li>Correct anything that is inaccurate</li>
                    <li>Delete your account and associated personal data</li>
                    <li>Stop sending you non-essential email</li>
                  </ul>
                  <p className="text-[#83928c] mt-4">
                    Write to privacy@tenses.ai from your registered email address and we will respond.
                    Deleting your account ends your access; as our Terms of Service explain, it does
                    not entitle you to a refund of any remaining period.
                  </p>
                </div>

                <div>
                  <h2 className="heading-3 mb-4">9. Children</h2>
                  <p className="text-[#83928c]">
                    Tenses is not intended for children under 13, and we do not knowingly collect
                    their personal information. If you believe a child has registered, contact us and
                    we will remove the account. Users under 18 should have a parent or guardian&apos;s
                    consent before purchasing a plan.
                  </p>
                </div>

                <div>
                  <h2 className="heading-3 mb-4">10. Cookies</h2>
                  <p className="text-[#83928c]">
                    We use cookies and similar technologies to keep you signed in and to understand
                    how the site is used. Our Cookie Policy explains what we set and how to control
                    it.
                  </p>
                </div>

                <div>
                  <h2 className="heading-3 mb-4">11. Changes to This Policy</h2>
                  <p className="text-[#83928c]">
                    We may update this policy as the platform develops. The revision date at the top
                    of this page always reflects the current version, and significant changes will be
                    highlighted on the site.
                  </p>
                </div>

                <div>
                  <h2 className="heading-3 mb-4">12. Contact Us</h2>
                  <p className="text-[#83928c]">
                    Questions about this Privacy Policy, or a request about your data, can be sent to
                    privacy@tenses.ai or through our Contact page.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPage;
